import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { TableFooterComponent } from '../../../../_shared/components/table-footer/table-footer.component';
import { LookupLabelPipe } from '../../../../_shared/pipes/lookup-label.pipe';
import { CostTrackingService } from '../../services/cost-tracking.service';
import { FilterService } from '../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { BudgetItem } from '../../models/cost-tracking-response.model';
import { BUDGET_CATEGORY_LABELS } from '../../models/cost-tracking-request.model';
import { WidgetFilterData } from '../../../../_shared/models/filters/widget-filter-data.model';
import { AuthService } from '../../../../_shared/services/auth.service';
import { formatCurrencyAmount } from '../../../../_shared/helpers/currency/currency-format.helper';

@Component({
  selector: 'app-budget-table',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, EmptyStateComponent, TableFooterComponent, TranslocoDirective, TranslocoPipe, LookupLabelPipe],
  templateUrl: './budget-table.component.html',
  styleUrl: './budget-table.component.scss'
})
export class BudgetTableComponent implements OnInit, OnChanges, OnDestroy {
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private confirmationModalService: ConfirmationModalService = inject(ConfirmationModalService);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);
  private authService: AuthService = inject(AuthService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private subscriptions: Subscription = new Subscription();

  /** Budgets are not date-bound: remember the last account scope so date-only filter changes do not reload. */
  private loadedAccountKey: string | null = null;

  @Input() searchTerm: string = '';

  @Input() budgetCategoryId: number | null = null;

  @Output() editBudget: EventEmitter<BudgetItem> = new EventEmitter<BudgetItem>();

  protected readonly budgetCategoryLabels = BUDGET_CATEGORY_LABELS;

  isLoading: boolean = false;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';

  budgets: BudgetItem[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;

  constructor() {
    this.currencySymbol = this.authService.getUserCurrencySymbol();

    this.subscriptions.add(this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (filter === null || Object.values(filter).every(x => x === null)) return;

      this.widgetFilter = filter;

      if (this.accountKey(filter) === this.loadedAccountKey) return;

      this.page = 1;
      await this.loadData();
    }));

    this.subscriptions.add(this.costTrackingService.budgetsChanged$.subscribe(async () => {
      await this.loadData();
    }));
  }

  async ngOnInit(): Promise<void> {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      await this.loadData();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const filterChanged = (changes['searchTerm'] && !changes['searchTerm'].firstChange)
      || (changes['budgetCategoryId'] && !changes['budgetCategoryId'].firstChange);

    if (filterChanged && this.widgetFilter) {
      this.page = 1;
      await this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const response = await this.costTrackingService.getBudgetTable({
        page: this.page,
        pageSize: this.pageSize,
        accountIds: this.widgetFilter?.accountIds ?? [],
        searchTerm: this.searchTerm?.trim() || undefined,
        budgetCategoryId: this.budgetCategoryId
      }, this.cancelSubject);

      this.loadedAccountKey = this.accountKey(this.widgetFilter);
      this.budgets = response.data?.results ?? [];
      this.totalCount = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;

      // Deleting the last row of the last page leaves `page` past the end: step back to the last real page.
      // Guarded on totalPages so an inconsistent response cannot loop (each retry strictly lowers `page`).
      if (!this.budgets.length && this.page > 1 && this.page > this.totalPages) {
        this.page = Math.max(1, this.totalPages);
        await this.loadData();
        return;
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private accountKey(filter: WidgetFilterData | null | undefined): string {
    return [...(filter?.accountIds ?? [])].sort((a, b) => a - b).join(',');
  }

  goToPage(page: number): void {
    this.page = page;
    this.loadData();
  }

  onEditClick(item: BudgetItem): void {
    this.editBudget.emit(item);
  }

  onDeleteClick(item: BudgetItem): void {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('costTracking.budgetTable.delete.title'),
      message: this.transloco.translate('costTracking.budgetTable.delete.message'),
      warningMessage: this.transloco.translate('costTracking.budgetTable.delete.warning'),
      confirmPlaceholder: this.transloco.translate('costTracking.budgetTable.delete.confirmPlaceholder'),
      confirmationPrompt: 'delete',
      confirmText: this.transloco.translate('costTracking.budgetTable.delete.confirmText'),
      declineText: this.transloco.translate('costTracking.budgetTable.delete.declineText'),
      onConfirm: async () => {
        try {
          await this.costTrackingService.deleteBudget(item.id);
          this.toastr.success(this.transloco.translate('costTracking.budgetTable.toasts.deleted'));
        } catch (error) {
          this.errorHandlerService.handleError(error);
          throw error; // keeps the confirmation modal open; it only closes when onConfirm resolves
        }
      }
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  assetLabel(item: BudgetItem): string {
    return item.assetName ?? this.transloco.translate('costTracking.budgetTable.fleet');
  }

  formatCost(value: number): string {
    return formatCurrencyAmount(this.currencySymbol, value);
  }
}